import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from '../admin/Modal';
import { Clock, Calendar, User, Trash2, Save, Check, X as XMark, AlertCircle, FileText, Pencil, Repeat, Ban, ChevronDown } from 'lucide-react';

// Custom Time Dropdown Component - scrolls to show current selection at top
interface TimeDropdownProps {
  value: string;
  onChange: (value: string) => void;
  timeSlots: { value: string; label: string }[];
  className?: string;
}

const TimeDropdown: React.FC<TimeDropdownProps> = ({ value, onChange, timeSlots, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);

  // Get the label for the current value
  const currentLabel = timeSlots.find(s => s.value === value)?.label || value;

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Scroll to selected item when opening
  useEffect(() => {
    if (isOpen && listRef.current && selectedRef.current) {
      // Scroll so selected item is at the top with a small offset
      const listTop = listRef.current.getBoundingClientRect().top;
      const selectedTop = selectedRef.current.getBoundingClientRect().top;
      const offset = selectedTop - listTop;
      listRef.current.scrollTop = offset - 4; // 4px padding from top
    }
  }, [isOpen]);

  const handleSelect = (val: string) => {
    onChange(val);
    setIsOpen(false);
  };

  return (
    <div className={`time-dropdown ${className || ''}`} ref={dropdownRef}>
      <button
        type="button"
        className={`time-dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{currentLabel}</span>
        <ChevronDown size={14} className={`dropdown-chevron ${isOpen ? 'rotated' : ''}`} />
      </button>
      {isOpen && (
        <div className="time-dropdown-list" ref={listRef}>
          {timeSlots.map(slot => (
            <div
              key={slot.value}
              ref={slot.value === value ? selectedRef : undefined}
              className={`time-dropdown-item ${slot.value === value ? 'selected' : ''}`}
              onClick={() => handleSelect(slot.value)}
            >
              {slot.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Participant in an appointment (organizer or attendee)
interface Participant {
  id: string;
  type: 'staff' | 'client';
  name: string;
  status?: 'pending' | 'confirmed' | 'declined' | 'tentative';
}

interface Appointment {
  id: string;
  organizer: Participant;
  attendees: Participant[];
  title: string;
  notes?: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no-show' | 'pending_approval';
  event_type?: 'appointment' | 'meeting' | 'personal';
  free_busy_status?: 'busy' | 'out_of_office' | 'tentative' | 'free';
  created_at: string;
  updated_at: string;
  // Series fields (Phase 7D)
  series_id?: string | null;
  series_rule?: {
    frequency: 'weekly' | 'biweekly' | 'monthly';
    count?: number;
    until?: string;
  } | null;
  instance_index?: number | null;
  is_exception?: boolean;
  // Booking metadata (Phase 5)
  booking?: {
    service_id?: string;
    requested_by_name?: string;
    requested_by_email?: string;
    requested_by_phone?: string;
    requested_by_type?: string;
    approval_status?: 'pending' | 'approved' | 'denied';
    session_method?: string;
    requested_at?: string;
  };
}

interface AppointmentEditModalProps {
  appointment: Appointment;
  clientName: string;
  clientTimezone?: string;
  coachTimezone?: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: () => void;
  onDeleted: () => void;
  onEditInTimeline?: (scope: 'single' | 'series') => void; // If provided, "Edit" button triggers timeline edit mode with scope
}

/**
 * APPOINTMENT EDIT MODAL - Phase 7B
 *
 * Modal for viewing, editing, and deleting existing appointments.
 * Features:
 * - View appointment details
 * - Edit title, date, time, duration
 * - Change status (complete, cancel, no-show)
 * - Delete with confirmation
 */
export const AppointmentEditModal: React.FC<AppointmentEditModalProps> = ({
  appointment,
  clientName,
  clientTimezone = 'America/New_York',
  coachTimezone = 'America/New_York',
  isOpen,
  onClose,
  onUpdated,
  onDeleted,
  onEditInTimeline
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Series action modal state (Phase 7D)
  const [showSeriesActionModal, setShowSeriesActionModal] = useState<'edit' | 'delete' | null>(null);

  // Determine current user's role on this appointment
  const currentUserId = (() => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return '';
      return JSON.parse(atob(token.split('.')[1])).userId || '';
    } catch { return ''; }
  })();
  const isOrganizer = appointment.organizer?.id === currentUserId;
  const myAttendeeRecord = (appointment.attendees || []).find(a => a.id === currentUserId);
  const isMeeting = appointment.event_type === 'meeting';

  // Check if this appointment is part of a series
  const isPartOfSeries = !!appointment.series_id;

  // Ref for auto-resizing textarea
  const notesTextareaRef = useRef<HTMLTextAreaElement>(null);
  // Ref for detecting scrollable notes in RO mode
  const notesContentRef = useRef<HTMLDivElement>(null);
  const [notesScrollable, setNotesScrollable] = useState(false);

  // Edit form state
  const [title, setTitle] = useState(appointment.title);
  const [notes, setNotes] = useState(appointment.notes || '');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(appointment.duration_minutes);
  const [status, setStatus] = useState(appointment.status);

  // Generate time slots for dropdown (7:00 AM to 8:00 PM in 15-min increments)
  const timeSlots = React.useMemo(() => {
    const slots: { value: string; label: string }[] = [];
    for (let hour = 7; hour <= 20; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const h24 = hour.toString().padStart(2, '0');
        const m = minute.toString().padStart(2, '0');
        const value = `${h24}:${m}`;
        const h12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const label = `${h12}:${m.padStart(2, '0')} ${ampm}`;
        slots.push({ value, label });
      }
    }
    return slots;
  }, []);

  // End date and time state (Outlook-style - both start and end are editable)
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');

  // Conflict checking state - API returns full appointment objects
  interface BusyBlock {
    id: string;
    start_time: string;
    end_time: string;
    title: string;
  }
  const [busyBlocks, setBusyBlocks] = useState<BusyBlock[]>([]);
  const [conflictWarning, setConflictWarning] = useState<string | null>(null);
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  // Tooltip state for read-only participant timeline
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    participantName: string;
    status: string;
  }>({ visible: false, x: 0, y: 0, participantName: '', status: '' });

  // Drag and resize state for timeline
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<'left' | 'right' | null>(null);
  const dragModeRef = useRef<'drag' | 'resize-left' | 'resize-right' | null>(null);
  const dragStartRef = useRef<{ x: number; startTime: string; endTimeVal: string; barWidth: number; barLeft: number } | null>(null);
  const timelineBarRef = useRef<HTMLDivElement>(null);

  // Timeline constants (7 AM to 8 PM = 13 hours)
  const TIMELINE_START = 7;
  const TIMELINE_END = 20;
  const TIMELINE_HOURS = TIMELINE_END - TIMELINE_START;

  // Convert percentage back to time string (HH:MM) with 15-minute snapping
  const percentToTime = useCallback((pct: number): string => {
    const clampedPct = Math.max(0, Math.min(100, pct));
    const totalMins = (clampedPct / 100) * (TIMELINE_HOURS * 60);
    const hours = Math.floor(totalMins / 60) + TIMELINE_START;
    const mins = totalMins % 60;
    // Snap to 15-minute increments
    const snappedMins = Math.round(mins / 15) * 15;
    const finalHours = Math.min(TIMELINE_END, Math.max(TIMELINE_START, hours + (snappedMins >= 60 ? 1 : 0)));
    const finalMins = snappedMins % 60;
    return `${finalHours.toString().padStart(2, '0')}:${finalMins.toString().padStart(2, '0')}`;
  }, [TIMELINE_HOURS, TIMELINE_START, TIMELINE_END]);

  // Convert time string (HH:MM) to percentage (for handlers)
  const timeToPercentHelper = useCallback((timeStr: string): number => {
    const [h, m] = timeStr.split(':').map(Number);
    const totalMins = (h - TIMELINE_START) * 60 + m;
    return Math.max(0, Math.min(100, (totalMins / (TIMELINE_HOURS * 60)) * 100));
  }, [TIMELINE_START, TIMELINE_HOURS]);

  // Handle mouse move during drag/resize (uses refs to avoid stale closure)
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragStartRef.current || !dragModeRef.current) return;

    const { barWidth, barLeft, startTime, endTimeVal } = dragStartRef.current;
    const mode = dragModeRef.current;

    // Get mouse position relative to the timeline bar
    const mouseX = e.clientX - barLeft;
    const mousePct = (mouseX / barWidth) * 100;

    if (mode === 'drag') {
      // Calculate the block width in percentage
      const startPct = timeToPercentHelper(startTime);
      const endPct = timeToPercentHelper(endTimeVal);
      const blockWidthPct = endPct - startPct;

      // Calculate new start based on mouse position (keep mouse at center of block)
      const dragOffset = (e.clientX - dragStartRef.current.x) / barWidth * 100;
      let newStartPct = startPct + dragOffset;
      let newEndPct = newStartPct + blockWidthPct;

      // Clamp to timeline bounds
      if (newStartPct < 0) {
        newStartPct = 0;
        newEndPct = blockWidthPct;
      }
      if (newEndPct > 100) {
        newEndPct = 100;
        newStartPct = 100 - blockWidthPct;
      }

      const newStart = percentToTime(newStartPct);
      const newEnd = percentToTime(newEndPct);
      setTime(newStart);
      setEndTime(newEnd);
    } else if (mode === 'resize-left') {
      // Resize from left - change start time only
      const endPct = timeToPercentHelper(endTimeVal);
      const minWidthPct = (15 / (TIMELINE_HOURS * 60)) * 100; // Minimum 15 minutes

      const newStartPct = Math.max(0, Math.min(endPct - minWidthPct, mousePct));
      const newStart = percentToTime(newStartPct);
      setTime(newStart);
    } else if (mode === 'resize-right') {
      // Resize from right - change end time only
      const startPct = timeToPercentHelper(startTime);
      const minWidthPct = (15 / (TIMELINE_HOURS * 60)) * 100; // Minimum 15 minutes

      const newEndPct = Math.max(startPct + minWidthPct, Math.min(100, mousePct));
      const newEnd = percentToTime(newEndPct);
      setEndTime(newEnd);
    }
  }, [percentToTime, timeToPercentHelper, TIMELINE_HOURS]);

  // Handle mouse up - stop dragging/resizing
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(null);
    dragModeRef.current = null;
    dragStartRef.current = null;
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  // Start drag or resize
  const handleDragStart = useCallback((e: React.MouseEvent, type: 'drag' | 'resize-left' | 'resize-right') => {
    e.stopPropagation();
    e.preventDefault();

    if (!timelineBarRef.current) return;

    const rect = timelineBarRef.current.getBoundingClientRect();

    // Set mode ref BEFORE adding listeners (avoids stale closure)
    dragModeRef.current = type;

    if (type === 'drag') {
      setIsDragging(true);
    } else {
      setIsResizing(type === 'resize-left' ? 'left' : 'right');
    }

    dragStartRef.current = {
      x: e.clientX,
      startTime: time,
      endTimeVal: endTime,
      barWidth: rect.width,
      barLeft: rect.left
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [time, endTime, handleMouseMove, handleMouseUp]);

  // Check for conflicts when date/time changes
  const checkConflicts = React.useCallback(async (checkDate: string, startTime: string, endT: string) => {
    if (!checkDate || !startTime || !endT) return;

    // Get the coach/organizer ID to check their availability
    const organizerId = appointment.organizer.id;

    setCheckingConflicts(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `/api/appointments/availability/${organizerId}?date=${checkDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // API returns appointments directly as busy_blocks
        const blocks = data.busy_blocks || [];
        setBusyBlocks(blocks);

        // Check if new time overlaps with any busy block (excluding current appointment)
        // Note: startTime/endT are in CLIENT timezone (from the form)
        const [startH, startM] = startTime.split(':').map(Number);
        const [endH, endM] = endT.split(':').map(Number);
        const newStartMins = startH * 60 + startM;
        const newEndMins = endH * 60 + endM;

        const conflict = blocks.find((block: BusyBlock) => {
          // Skip the current appointment
          if (block.id === appointment.id) return false;

          // Convert block times to CLIENT timezone for comparison
          // (block times are stored as UTC ISO strings)
          const blockStart = new Date(block.start_time);
          const blockEnd = new Date(block.end_time);

          // Extract hours/minutes in CLIENT timezone (not browser local time)
          const blockStartH = parseInt(blockStart.toLocaleString('en-US', { timeZone: clientTimezone, hour: '2-digit', hour12: false }), 10);
          const blockStartM = parseInt(blockStart.toLocaleString('en-US', { timeZone: clientTimezone, minute: '2-digit' }), 10);
          const blockEndH = parseInt(blockEnd.toLocaleString('en-US', { timeZone: clientTimezone, hour: '2-digit', hour12: false }), 10);
          const blockEndM = parseInt(blockEnd.toLocaleString('en-US', { timeZone: clientTimezone, minute: '2-digit' }), 10);

          const blockStartMins = blockStartH * 60 + blockStartM;
          const blockEndMins = blockEndH * 60 + blockEndM;

          // Check overlap
          return newStartMins < blockEndMins && newEndMins > blockStartMins;
        });

        if (conflict) {
          // Display conflict time in CLIENT timezone (consistent with timeline)
          const conflictStart = new Date(conflict.start_time);
          const conflictTime = conflictStart.toLocaleTimeString('en-US', {
            timeZone: clientTimezone,
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          setConflictWarning(`Conflicts with "${conflict.title}" at ${conflictTime}`);
        } else {
          setConflictWarning(null);
        }
      }
    } catch (err) {
      console.error('[AppointmentEdit] Error checking conflicts:', err);
    } finally {
      setCheckingConflicts(false);
    }
  }, [appointment.organizer.id, appointment.id, clientTimezone]);

  // Check conflicts when entering edit mode or changing date/time
  React.useEffect(() => {
    if (isEditing && date && time && endTime) {
      checkConflicts(date, time, endTime);
    }
  }, [isEditing, date, time, endTime, checkConflicts]);

  // Calculate duration when end time changes
  const calculateDuration = (startD: string, startT: string, endD: string, endT: string) => {
    if (!startD || !startT || !endD || !endT) return 30;
    const start = new Date(`${startD}T${startT}:00`);
    const end = new Date(`${endD}T${endT}:00`);
    const diffMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));
    return Math.max(15, diffMinutes); // Minimum 15 minutes
  };

  // Update end time when start time changes (preserve duration)
  const handleStartTimeChange = (newTime: string) => {
    const currentDuration = calculateDuration(date, time, endDate, endTime);
    setTime(newTime);
    // Update end time to maintain same duration
    const newStart = new Date(`${date}T${newTime}:00`);
    const newEnd = new Date(newStart.getTime() + currentDuration * 60 * 1000);
    const endH = newEnd.getHours().toString().padStart(2, '0');
    const endM = newEnd.getMinutes().toString().padStart(2, '0');
    setEndTime(`${endH}:${endM}`);
    setEndDate(newEnd.toISOString().split('T')[0]);
  };

  // Update end date when start date changes (preserve duration)
  const handleStartDateChange = (newDate: string) => {
    const currentDuration = calculateDuration(date, time, endDate, endTime);
    setDate(newDate);
    // Update end date to maintain same duration
    const newStart = new Date(`${newDate}T${time}:00`);
    const newEnd = new Date(newStart.getTime() + currentDuration * 60 * 1000);
    setEndDate(newEnd.toISOString().split('T')[0]);
  };

  // Update duration state when end time changes
  const handleEndTimeChange = (newEndTime: string) => {
    setEndTime(newEndTime);
    const newDuration = calculateDuration(date, time, endDate, newEndTime);
    setDuration(newDuration);
  };

  // Update duration state when end date changes
  const handleEndDateChange = (newEndDate: string) => {
    setEndDate(newEndDate);
    const newDuration = calculateDuration(date, time, newEndDate, endTime);
    setDuration(newDuration);
  };

  // Auto-resize textarea based on content
  const autoResizeTextarea = useCallback(() => {
    const textarea = notesTextareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get accurate scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height (min 80px, max 300px before scrolling)
    const minHeight = 80;
    const maxHeight = 300;
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);

    textarea.style.height = `${newHeight}px`;

    // Enable scrolling if content exceeds max height
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  // Auto-resize when notes change or editing starts
  useEffect(() => {
    if (isEditingNotes) {
      // Small delay to ensure textarea is rendered
      setTimeout(autoResizeTextarea, 0);
    }
  }, [isEditingNotes, notes, autoResizeTextarea]);

  // Check if RO notes content is scrollable
  useEffect(() => {
    if (!isEditingNotes && notesContentRef.current) {
      const el = notesContentRef.current;
      setNotesScrollable(el.scrollHeight > el.clientHeight);
    }
  }, [isEditingNotes, notes]);

  // Initialize date/time from appointment (both start and end)
  // Also resets when isOpen changes (modal reopens) to clear any unsaved changes
  useEffect(() => {
    if (!isOpen) return; // Only initialize when modal is open

    // Start time
    const startDateTime = new Date(appointment.start_time);
    const localStart = new Date(startDateTime.toLocaleString('en-US', { timeZone: clientTimezone }));
    const startDateStr = startDateTime.toLocaleDateString('en-CA', { timeZone: clientTimezone });
    setDate(startDateStr);

    // Round start time to nearest 15 minutes
    const startHours = localStart.getHours();
    const startRawMins = localStart.getMinutes();
    const startRoundedMins = Math.round(startRawMins / 15) * 15;
    const startFinalHours = startRoundedMins === 60 ? startHours + 1 : startHours;
    const startFinalMins = startRoundedMins === 60 ? 0 : startRoundedMins;
    setTime(`${startFinalHours.toString().padStart(2, '0')}:${startFinalMins.toString().padStart(2, '0')}`);

    // End time
    const endDateTime = new Date(appointment.end_time);
    const localEnd = new Date(endDateTime.toLocaleString('en-US', { timeZone: clientTimezone }));
    const endDateStr = endDateTime.toLocaleDateString('en-CA', { timeZone: clientTimezone });
    setEndDate(endDateStr);

    // Round end time to nearest 15 minutes
    const endHours = localEnd.getHours();
    const endRawMins = localEnd.getMinutes();
    const endRoundedMins = Math.round(endRawMins / 15) * 15;
    const endFinalHours = endRoundedMins === 60 ? endHours + 1 : endHours;
    const endFinalMins = endRoundedMins === 60 ? 0 : endRoundedMins;
    setEndTime(`${endFinalHours.toString().padStart(2, '0')}:${endFinalMins.toString().padStart(2, '0')}`);
  }, [appointment, clientTimezone, isOpen]);

  // Reset form when appointment changes or modal reopens
  // This ensures cancelled changes don't persist when reopening the modal
  useEffect(() => {
    if (!isOpen) return; // Only reset when modal is open

    setTitle(appointment.title);
    setNotes(appointment.notes || '');
    setDuration(appointment.duration_minutes);
    setStatus(appointment.status);
    setIsEditing(false);
    setIsEditingNotes(false);
    setShowDeleteConfirm(false);
    setError(null);
    setConflictWarning(null);
  }, [appointment, isOpen]);

  // Helper function to reset all form state to original appointment values
  // Used by Cancel button to discard unsaved changes
  const resetFormToAppointment = useCallback(() => {
    // Reset basic fields
    setTitle(appointment.title);
    setNotes(appointment.notes || '');
    setDuration(appointment.duration_minutes);
    setStatus(appointment.status);
    setConflictWarning(null);
    setError(null);

    // Reset date/time fields (same logic as initialization useEffect)
    const startDateTime = new Date(appointment.start_time);
    const localStart = new Date(startDateTime.toLocaleString('en-US', { timeZone: clientTimezone }));
    const startDateStr = startDateTime.toLocaleDateString('en-CA', { timeZone: clientTimezone });
    setDate(startDateStr);

    const startHours = localStart.getHours();
    const startRawMins = localStart.getMinutes();
    const startRoundedMins = Math.round(startRawMins / 15) * 15;
    const startFinalHours = startRoundedMins === 60 ? startHours + 1 : startHours;
    const startFinalMins = startRoundedMins === 60 ? 0 : startRoundedMins;
    setTime(`${startFinalHours.toString().padStart(2, '0')}:${startFinalMins.toString().padStart(2, '0')}`);

    const endDateTime = new Date(appointment.end_time);
    const localEnd = new Date(endDateTime.toLocaleString('en-US', { timeZone: clientTimezone }));
    const endDateStr = endDateTime.toLocaleDateString('en-CA', { timeZone: clientTimezone });
    setEndDate(endDateStr);

    const endHours = localEnd.getHours();
    const endRawMins = localEnd.getMinutes();
    const endRoundedMins = Math.round(endRawMins / 15) * 15;
    const endFinalHours = endRoundedMins === 60 ? endHours + 1 : endHours;
    const endFinalMins = endRoundedMins === 60 ? 0 : endRoundedMins;
    setEndTime(`${endFinalHours.toString().padStart(2, '0')}:${endFinalMins.toString().padStart(2, '0')}`);
  }, [appointment, clientTimezone]);

  // Get timezone abbreviation
  const getTimezoneAbbr = (tz: string, forDate?: Date) => {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(forDate || new Date());
      return parts.find(p => p.type === 'timeZoneName')?.value || tz;
    } catch {
      return tz;
    }
  };

  // Check if two timezones have the same offset at a given time
  const timezonesMatch = (tz1: string, tz2: string, forDate: Date): boolean => {
    const abbr1 = getTimezoneAbbr(tz1, forDate);
    const abbr2 = getTimezoneAbbr(tz2, forDate);
    return abbr1 === abbr2;
  };

  // Format time for display in a specific timezone
  const formatTimeInTz = (isoString: string, tz: string) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: tz
    });
  };

  // Format date for display (without time)
  const formatDateOnly = (isoString: string, tz: string) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: tz
    });
  };

  // Format HH:MM time string as "12:00 PM" (no timezone conversion)
  const formatTime12h = (timeStr: string): string => {
    const [h, m] = timeStr.split(':').map(Number);
    const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  // Convert a time from one timezone to another on a given date
  // timeStr is "HH:MM", dateStr is "YYYY-MM-DD"
  const convertTimeBetweenTimezones = (
    timeStr: string,
    dateStr: string,
    fromTz: string,
    toTz: string
  ): string => {
    const [h, m] = timeStr.split(':').map(Number);

    // Use noon UTC as reference point to calculate offset between timezones
    const refDateUtc = new Date(`${dateStr}T12:00:00Z`);

    // Get what hour it is in each timezone at the same UTC moment
    const hourInFromTz = parseInt(refDateUtc.toLocaleString('en-US', { timeZone: fromTz, hour: '2-digit', hour12: false }), 10);
    const hourInToTz = parseInt(refDateUtc.toLocaleString('en-US', { timeZone: toTz, hour: '2-digit', hour12: false }), 10);

    // Calculate the offset: how many hours ahead is toTz compared to fromTz
    const offsetHours = hourInToTz - hourInFromTz;

    // Apply offset to the input time
    let newH = h + offsetHours;
    const newM = m;

    // Handle day wrap-around
    if (newH >= 24) newH -= 24;
    if (newH < 0) newH += 24;

    // Format as 12-hour time
    const hour12 = newH > 12 ? newH - 12 : newH === 0 ? 12 : newH;
    const ampm = newH >= 12 ? 'PM' : 'AM';
    return `${hour12}:${newM.toString().padStart(2, '0')} ${ampm}`;
  };

  // Status badge styling
  const getStatusBadge = (s: string) => {
    const styles: Record<string, { bg: string; color: string; label: string }> = {
      scheduled: { bg: 'rgba(16, 185, 129, 0.15)', color: '#10b981', label: 'Scheduled' },
      completed: { bg: 'rgba(107, 114, 128, 0.15)', color: '#6b7280', label: 'Completed' },
      cancelled: { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', label: 'Cancelled' },
      'no-show': { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', label: 'No-Show' },
      'pending_approval': { bg: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', label: 'Pending Approval' }
    };
    return styles[s] || styles.scheduled;
  };

  // Handle save
  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');

      // Create start/end dates from form state
      const startDateTime = new Date(`${date}T${time}:00`);
      const endDateTime = new Date(`${endDate}T${endTime}:00`);

      // Convert from client timezone to UTC
      const clientTzOffset = new Date(startDateTime.toLocaleString('en-US', { timeZone: clientTimezone })).getTime() - startDateTime.getTime();
      const utcStart = new Date(startDateTime.getTime() - clientTzOffset);
      const utcEnd = new Date(endDateTime.getTime() - clientTzOffset);

      // Calculate duration from start/end
      const calculatedDuration = Math.round((utcEnd.getTime() - utcStart.getTime()) / (1000 * 60));

      const response = await fetch(`/api/appointments/${appointment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title,
          notes,
          start_time: utcStart.toISOString(),
          end_time: utcEnd.toISOString(),
          duration_minutes: calculatedDuration,
          status
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update appointment');
      }

      setIsEditing(false);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  // Handle notes save (quick action)
  const handleNotesSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/appointments/${appointment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          notes
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save notes');
      }

      // Just exit edit mode - notes state already has the new value
      // Don't call onUpdated() as that would refresh/close the modal
      setIsEditingNotes(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save notes');
    } finally {
      setLoading(false);
    }
  };

  // Handle status change (quick action)
  const handleStatusChange = async (newStatus: string) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/appointments/${appointment.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update status');
      }

      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/appointments/${appointment.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete appointment');
      }

      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle series cancellation (cancel this and all future)
  const handleSeriesCancel = async () => {
    if (!appointment.series_id) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/appointments/series/${appointment.series_id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          from_instance: appointment.instance_index ?? 0
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to cancel series');
      }

      setShowSeriesActionModal(null);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel series');
    } finally {
      setLoading(false);
    }
  };

  // Handle series deletion (delete this and all future - permanently removes from calendar)
  const handleSeriesDelete = async () => {
    if (!appointment.series_id) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/appointments/series/${appointment.series_id}/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          from_instance: appointment.instance_index ?? 0
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete series');
      }

      setShowSeriesActionModal(null);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete series');
    } finally {
      setLoading(false);
    }
  };

  // Handle just this one (mark as exception and delete)
  const handleDeleteJustThisOne = async () => {
    // First mark as exception if part of series
    if (isPartOfSeries) {
      try {
        const token = localStorage.getItem('auth_token');
        await fetch(`/api/appointments/${appointment.id}/mark-exception`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch {
        // Continue with delete even if marking fails
      }
    }
    setShowSeriesActionModal(null);
    setShowDeleteConfirm(true);
  };

  // Handle cancel single appointment (sets status to cancelled, stays on calendar)
  const handleCancelSingle = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/appointments/${appointment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'cancelled'
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to cancel appointment');
      }

      setShowSeriesActionModal(null);
      onDeleted(); // Refresh the calendar
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel appointment');
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel just this one in a series (mark as exception, then cancel)
  const handleCancelJustThisOne = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');

      // First mark as exception if part of series
      if (isPartOfSeries) {
        await fetch(`/api/appointments/${appointment.id}/mark-exception`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }

      // Then cancel it
      const response = await fetch(`/api/appointments/${appointment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'cancelled'
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to cancel appointment');
      }

      setShowSeriesActionModal(null);
      onDeleted(); // Refresh the calendar
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel appointment');
    } finally {
      setLoading(false);
    }
  };

  // Handle attendee response (accept/tentative/decline)
  const handleAttendeeResponse = async (response: 'confirmed' | 'tentative' | 'declined') => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`/api/appointments/${appointment.id}/attendees/${currentUserId}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: response })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update response');
      }
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update response');
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = getStatusBadge(appointment.status);
  const tzAbbr = getTimezoneAbbr(clientTimezone);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Appointment' : 'Appointment Details'}
      size="small"
      hasUnsavedChanges={isEditing}
      footer={
        showDeleteConfirm ? (
          <div className="delete-confirm-footer">
            <span className="confirm-text">Delete this appointment?</span>
            <button
              className="btn btn-secondary"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Yes, Delete'}
            </button>
          </div>
        ) : isEditing ? (
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsEditing(false);
                resetFormToAppointment();
              }}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={loading}
            >
              <Save size={16} />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        ) : (
          <>
            {/* Attendee on a meeting — show response buttons instead of organizer actions */}
            {isMeeting && !isOrganizer && myAttendeeRecord ? (
              <>
                <button className="btn btn-primary" onClick={onClose}>Close</button>
              </>
            ) : (
              <>
                <button
                  className="btn btn-danger-filled"
                  onClick={() => isPartOfSeries ? setShowSeriesActionModal('delete') : setShowDeleteConfirm(true)}
                >
                  <Trash2 size={16} />
                  Delete
                </button>
                {appointment.status !== 'cancelled' && (
                  <button
                    className="btn btn-warning"
                    onClick={() => isPartOfSeries ? setShowSeriesActionModal('cancel') : handleCancelSingle()}
                  >
                    <Ban size={16} />
                    Cancel
                  </button>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    if (isPartOfSeries) {
                      setShowSeriesActionModal('edit');
                    } else {
                      onEditInTimeline ? onEditInTimeline('single') : setIsEditing(true);
                    }
                  }}
                >
                  Edit
                </button>
                <button className="btn btn-primary" onClick={onClose}>
                  Close
                </button>
              </>
            )}
          </>
        )
      }
    >
      <div className="appointment-edit-content">
        {error && (
          <div className="error-banner">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {isEditing ? (
          // Edit Form - Clean Outlook-style layout
          <div className="edit-form outlook-style">
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Appointment title"
              />
            </div>

            {/* Outlook-style date/time picker */}
            <div className="datetime-picker-group">
              {/* Start Row */}
              <div className="datetime-row">
                <span className="datetime-label">Start</span>
                <input
                  type="date"
                  value={date}
                  onChange={e => handleStartDateChange(e.target.value)}
                  className="datetime-date"
                />
                <TimeDropdown
                  value={time}
                  onChange={handleStartTimeChange}
                  timeSlots={timeSlots}
                  className="datetime-time"
                />
              </div>

              {/* End Row */}
              <div className="datetime-row">
                <span className="datetime-label">End</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => handleEndDateChange(e.target.value)}
                  className="datetime-date"
                />
                <TimeDropdown
                  value={endTime}
                  onChange={handleEndTimeChange}
                  timeSlots={timeSlots}
                  className="datetime-time"
                />
              </div>

              {/* Duration indicator */}
              <div className="duration-indicator">
                <Clock size={12} />
                <span>{duration} minutes</span>
                <span className="tz-hint">({tzAbbr})</span>
              </div>

              {/* Conflict Warning */}
              {conflictWarning && (
                <div className="conflict-warning">
                  <AlertCircle size={14} />
                  <span>{conflictWarning}</span>
                </div>
              )}

              {/* Visual Timeline - Scheduling Assistant Style */}
              {(() => {
                // Timeline runs from 7 AM to 8 PM (13 hours)
                const TIMELINE_START = 7; // 7 AM
                const TIMELINE_END = 20;  // 8 PM
                const TIMELINE_HOURS = TIMELINE_END - TIMELINE_START;

                // Convert time string (HH:MM) to position percentage
                const timeToPercent = (timeStr: string): number => {
                  const [h, m] = timeStr.split(':').map(Number);
                  const totalMins = (h - TIMELINE_START) * 60 + m;
                  return Math.max(0, Math.min(100, (totalMins / (TIMELINE_HOURS * 60)) * 100));
                };

                // Get position from ISO datetime (converted to client timezone)
                const isoToPercent = (isoStr: string): number => {
                  const d = new Date(isoStr);
                  // Extract hours and minutes in the client's timezone
                  const h = parseInt(d.toLocaleString('en-US', { timeZone: clientTimezone, hour: '2-digit', hour12: false }), 10);
                  const m = parseInt(d.toLocaleString('en-US', { timeZone: clientTimezone, minute: '2-digit' }), 10);
                  const totalMins = (h - TIMELINE_START) * 60 + m;
                  return Math.max(0, Math.min(100, (totalMins / (TIMELINE_HOURS * 60)) * 100));
                };

                // Proposed appointment position
                const proposedStart = timeToPercent(time);
                const proposedEnd = timeToPercent(endTime);
                const proposedWidth = Math.max(2, proposedEnd - proposedStart);

                // Filter out current appointment from busy blocks
                const otherBlocks = busyBlocks.filter(b => b.id !== appointment.id);

                // Hour markers for the timeline (every hour)
                const hourMarkers = [];
                for (let h = TIMELINE_START; h <= TIMELINE_END; h += 1) {
                  const pct = ((h - TIMELINE_START) / TIMELINE_HOURS) * 100;
                  const label = h === 12 ? '12p' : h > 12 ? `${h - 12}p` : `${h}a`;
                  hourMarkers.push({ pct, label, hour: h });
                }

                // Half-hour markers (for dotted lines at :30)
                const halfHourMarkers = [];
                for (let h = TIMELINE_START; h < TIMELINE_END; h += 1) {
                  const pct = ((h - TIMELINE_START + 0.5) / TIMELINE_HOURS) * 100;
                  halfHourMarkers.push({ pct, hour: h });
                }

                // Build tooltip for proposed time with timezone info
                // Note: time and endTime are already in client timezone, so format directly
                const proposedClientTzAbbr = getTimezoneAbbr(clientTimezone, new Date(`${date}T12:00:00Z`));
                const proposedCoachTzAbbr = getTimezoneAbbr(coachTimezone, new Date(`${date}T12:00:00Z`));
                const proposedTzDiffers = !timezonesMatch(clientTimezone, coachTimezone, new Date(`${date}T12:00:00Z`));

                // Client time: just format the time string directly (already in client TZ)
                const proposedStartTime = formatTime12h(time);
                const proposedEndTime = formatTime12h(endTime);

                let proposedTooltip = `${title}\n${proposedStartTime} - ${proposedEndTime} (${proposedClientTzAbbr})`;
                if (proposedTzDiffers) {
                  // Coach time: convert from client TZ to coach TZ
                  const proposedCoachStart = convertTimeBetweenTimezones(time, date, clientTimezone, coachTimezone);
                  const proposedCoachEnd = convertTimeBetweenTimezones(endTime, date, clientTimezone, coachTimezone);
                  proposedTooltip += `\n[${proposedCoachStart} - ${proposedCoachEnd} ${proposedCoachTzAbbr} for you]`;
                }

                // Calculate width factor: show 6 hours in view, so total is (totalHours/6) * 100%
                const VISIBLE_HOURS = 6;
                const widthFactor = TIMELINE_HOURS / VISIBLE_HOURS;
                const totalWidthPct = widthFactor * 100;

                // Calculate scroll position to center on proposed time
                const proposedCenterPct = (timeToPercent(time) + timeToPercent(endTime)) / 2;
                const scrollToCenterPct = Math.max(0, Math.min(100, proposedCenterPct));

                return (
                  <div className="scheduling-timeline">
                    <div className="timeline-header">
                      <User size={12} />
                      <span>Schedule for {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                      {checkingConflicts && <span className="loading-indicator">...</span>}
                    </div>

                    <div
                      className="timeline-bar-container"
                      ref={(el) => {
                        // Auto-scroll to center on proposed time
                        if (el && !checkingConflicts) {
                          const scrollLeft = (scrollToCenterPct / 100) * el.scrollWidth - (el.clientWidth / 2);
                          el.scrollLeft = Math.max(0, scrollLeft);
                        }
                      }}
                    >
                      <div className="timeline-inner" style={{ width: `${totalWidthPct}%` }}>
                        {/* Hour markers */}
                        <div className="timeline-markers">
                          {hourMarkers.map(m => (
                            <div key={m.hour} className="hour-marker" style={{ left: `${m.pct}%` }}>
                              <span className="marker-label">{m.label}</span>
                            </div>
                          ))}
                        </div>

                        {/* Timeline bar */}
                        <div className="timeline-bar" ref={timelineBarRef}>
                        {/* Hour divider lines */}
                        {hourMarkers.map(m => (
                          <div
                            key={`divider-${m.hour}`}
                            className="timeline-divider"
                            style={{ left: `${m.pct}%` }}
                          />
                        ))}

                        {/* Half-hour divider lines (dotted) */}
                        {halfHourMarkers.map(m => (
                          <div
                            key={`half-divider-${m.hour}`}
                            className="timeline-divider half-hour"
                            style={{ left: `${m.pct}%` }}
                          />
                        ))}

                        {/* Busy blocks (other appointments) */}
                        {otherBlocks.map((block, idx) => {
                          const start = isoToPercent(block.start_time);
                          const end = isoToPercent(block.end_time);
                          const width = Math.max(2, end - start);

                          // Build tooltip with timezone info
                          const blockDate = new Date(block.start_time);
                          const clientStartTime = formatTimeInTz(block.start_time, clientTimezone);
                          const clientEndTime = formatTimeInTz(block.end_time, clientTimezone);
                          const clientTzAbbr = getTimezoneAbbr(clientTimezone, blockDate);
                          const tzDiffers = !timezonesMatch(clientTimezone, coachTimezone, blockDate);

                          let tooltipText = `${block.title}\n${clientStartTime} - ${clientEndTime} (${clientTzAbbr})`;
                          if (tzDiffers) {
                            const coachStartTime = formatTimeInTz(block.start_time, coachTimezone);
                            const coachEndTime = formatTimeInTz(block.end_time, coachTimezone);
                            const coachTzAbbr = getTimezoneAbbr(coachTimezone, blockDate);
                            tooltipText += `\n[${coachStartTime} - ${coachEndTime} ${coachTzAbbr} for you]`;
                          }

                          return (
                            <div
                              key={idx}
                              className="timeline-busy"
                              style={{ left: `${start}%`, width: `${width}%` }}
                              title={tooltipText}
                            />
                          );
                        })}

                        {/* Proposed time (current selection) - draggable with resize handles */}
                        <div
                          className={`timeline-proposed ${conflictWarning ? 'has-conflict' : ''} ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''}`}
                          style={{ left: `${proposedStart}%`, width: `${proposedWidth}%` }}
                          title={proposedTooltip}
                          onMouseDown={(e) => handleDragStart(e, 'drag')}
                        >
                          {/* Left resize handle */}
                          <div
                            className="resize-handle resize-handle-left"
                            onMouseDown={(e) => handleDragStart(e, 'resize-left')}
                          />
                          {/* Right resize handle */}
                          <div
                            className="resize-handle resize-handle-right"
                            onMouseDown={(e) => handleDragStart(e, 'resize-right')}
                          />
                        </div>
                        </div>
                      </div>
                    </div>

                    {/* Legend */}
                    <div className="timeline-legend">
                      <span className="legend-item">
                        <span className="legend-swatch proposed" />
                        This appointment
                      </span>
                      {otherBlocks.length > 0 && (
                        <span className="legend-item">
                          <span className="legend-swatch busy" />
                          Busy ({otherBlocks.length})
                        </span>
                      )}
                      {otherBlocks.length === 0 && (
                        <span className="legend-item free">
                          <Check size={10} /> Day is open
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="form-group">
              <label>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value as typeof status)}>
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="no-show">No-Show</option>
              </select>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Optional notes..."
                rows={3}
              />
            </div>
          </div>
        ) : (
          // View Mode
          <div className="view-details">
            <div className="detail-header">
              <h3>{appointment.title}</h3>
              <span
                className="status-badge"
                style={{ background: statusBadge.bg, color: statusBadge.color }}
              >
                {statusBadge.label}
              </span>
            </div>

            {appointment.event_type !== 'personal' && clientName && clientName !== 'Unknown Client' && (
              <div className="detail-row">
                <User size={16} />
                <span>{clientName}</span>
              </div>
            )}

            {appointment.event_type === 'personal' && appointment.free_busy_status && (
              <div className="detail-row">
                <Clock size={16} />
                <span>Show as: {appointment.free_busy_status === 'out_of_office' ? 'Out of Office' : appointment.free_busy_status.charAt(0).toUpperCase() + appointment.free_busy_status.slice(1)}</span>
              </div>
            )}

            {/* Date/Time with timezone clarity */}
            {(() => {
              const apptDate = new Date(appointment.start_time);
              const clientTzAbbr = getTimezoneAbbr(clientTimezone, apptDate);
              const coachTzAbbr = getTimezoneAbbr(coachTimezone, apptDate);
              const tzDiffers = !timezonesMatch(clientTimezone, coachTimezone, apptDate);
              const clientTime = formatTimeInTz(appointment.start_time, clientTimezone);
              const coachTime = formatTimeInTz(appointment.start_time, coachTimezone);
              const dateStr = formatDateOnly(appointment.start_time, clientTimezone);

              return (
                <div className="detail-row datetime-block">
                  <Calendar size={16} />
                  <div className="datetime-info">
                    <span className="primary-time">
                      {dateStr}, {clientTime} ({clientTzAbbr})
                    </span>
                    {tzDiffers && (
                      <span className="secondary-time">
                        [{coachTime} {coachTzAbbr} for you]
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="detail-row">
              <Clock size={16} />
              <span>{appointment.duration_minutes} minutes</span>
            </div>

            {/* Compact Participant Timeline (Read-Only) */}
            {(() => {
              // Calculate time window: 1 hour before to 1 hour after the appointment
              const apptStart = new Date(appointment.start_time);
              const apptEnd = new Date(appointment.end_time);

              // Get appointment time in client timezone
              const startHour = parseInt(apptStart.toLocaleString('en-US', { timeZone: clientTimezone, hour: '2-digit', hour12: false }), 10);
              const startMin = parseInt(apptStart.toLocaleString('en-US', { timeZone: clientTimezone, minute: '2-digit' }), 10);
              const endHour = parseInt(apptEnd.toLocaleString('en-US', { timeZone: clientTimezone, hour: '2-digit', hour12: false }), 10);
              const endMin = parseInt(apptEnd.toLocaleString('en-US', { timeZone: clientTimezone, minute: '2-digit' }), 10);

              // Window: 1 hour before start to 1 hour after end (min 3 hours shown)
              const windowStart = Math.max(0, startHour - 1);
              const windowEnd = Math.min(24, Math.max(endHour + 1, windowStart + 3));
              const windowHours = windowEnd - windowStart;

              // Calculate position of appointment within window
              const startMins = (startHour - windowStart) * 60 + startMin;
              const endMins = (endHour - windowStart) * 60 + endMin;
              const totalMins = windowHours * 60;
              const blockLeft = (startMins / totalMins) * 100;
              const blockWidth = Math.max(5, ((endMins - startMins) / totalMins) * 100);

              const getStatusLabel = (status: string) => {
                switch (status) {
                  case 'confirmed': return 'Confirmed';
                  case 'tentative': return 'Tentative';
                  case 'declined': return 'Declined';
                  default: return 'Pending';
                }
              };

              // Handle block hover for tooltip
              const handleBlockHover = (e: React.MouseEvent, participantName: string, status: string) => {
                setTooltip({
                  visible: true,
                  x: e.clientX,
                  y: e.clientY,
                  participantName,
                  status
                });
              };

              const handleBlockLeave = () => {
                setTooltip({ visible: false, x: 0, y: 0, participantName: '', status: '' });
              };

              // Generate hour markers
              const markers = [];
              for (let h = windowStart; h <= windowEnd; h++) {
                const pct = ((h - windowStart) / windowHours) * 100;
                const label = h === 0 ? '12 AM' : h === 12 ? '12 PM' : h > 12 ? `${h - 12} PM` : `${h} AM`;
                markers.push({ hour: h, pct, label });
              }

              // Generate half-hour markers (dotted lines)
              const halfHourMarkers = [];
              for (let h = windowStart; h < windowEnd; h++) {
                const pct = ((h - windowStart + 0.5) / windowHours) * 100;
                halfHourMarkers.push({ hour: h + 0.5, pct });
              }

              // Build list of all participants (organizer + attendees)
              const allParticipants = [
                {
                  id: appointment.organizer.id,
                  name: appointment.organizer.name || 'Organizer',
                  status: 'confirmed' as const,
                  isOrganizer: true
                },
                ...appointment.attendees.map(a => ({
                  id: a.id,
                  name: a.name,
                  status: a.status || 'pending',
                  isOrganizer: false
                }))
              ];

              return (
                <div className="participant-timeline-compact">
                  {/* Hour markers */}
                  <div className="timeline-hours">
                    {markers.map(m => (
                      <span key={m.hour} className="hour-mark" style={{ left: `${m.pct}%` }}>
                        {m.label}
                      </span>
                    ))}
                  </div>

                  {/* Scrollable participant rows */}
                  <div className="participant-rows-container">
                    {allParticipants.map((participant, idx) => (
                      <div key={participant.id || idx} className="participant-row">
                        <div className="participant-label">
                          <span className="participant-name">{participant.name.split(' ')[0]}</span>
                          <span className={`participant-status ${participant.status}`}>
                            {participant.isOrganizer ? 'Organizer' : getStatusLabel(participant.status)}
                          </span>
                        </div>
                        <div className="participant-bar">
                          {markers.map(m => (
                            <div key={`${idx}-h-${m.hour}`} className="bar-divider" style={{ left: `${m.pct}%` }} />
                          ))}
                          {halfHourMarkers.map(m => (
                            <div key={`${idx}-hh-${m.hour}`} className="bar-divider half-hour" style={{ left: `${m.pct}%` }} />
                          ))}
                          <div
                            className={`appointment-block ${participant.isOrganizer ? 'confirmed' : participant.status}`}
                            style={{ left: `${blockLeft}%`, width: `${blockWidth}%` }}
                            onMouseEnter={(e) => handleBlockHover(
                              e,
                              participant.name,
                              participant.isOrganizer ? 'Organizer' : getStatusLabel(participant.status)
                            )}
                            onMouseLeave={handleBlockLeave}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Series indicator */}
            {isPartOfSeries && (
              <div className="detail-row series-badge">
                <Repeat size={16} />
                <span>
                  Part of recurring series
                  {appointment.instance_index !== undefined && appointment.instance_index !== null && (
                    <> (#{appointment.instance_index + 1})</>
                  )}
                  {appointment.is_exception && ' - Modified'}
                </span>
              </div>
            )}

            {/* Notes section - always visible */}
            <div className="detail-notes">
              <div className="notes-header">
                <div className="notes-label">
                  <FileText size={14} />
                  <span>Notes</span>
                </div>
                {!isEditingNotes && (
                  <button
                    className="edit-notes-btn"
                    onClick={() => setIsEditingNotes(true)}
                    title="Edit notes"
                  >
                    <Pencil size={12} />
                  </button>
                )}
              </div>
              {isEditingNotes ? (
                <div className="notes-edit-inline">
                  <div className="textarea-container">
                    <textarea
                      ref={notesTextareaRef}
                      value={notes}
                      onChange={e => {
                        setNotes(e.target.value);
                        autoResizeTextarea();
                      }}
                      placeholder="Add notes about this appointment..."
                      autoFocus
                    />
                    <div className="resize-grip">
                      <svg width="10" height="10" viewBox="0 0 10 10">
                        <path d="M9 1L1 9M9 5L5 9M9 9L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </div>
                  </div>
                  <div className="notes-edit-actions">
                    <button
                      className="btn-inline cancel"
                      onClick={() => {
                        setNotes(appointment.notes || '');
                        setIsEditingNotes(false);
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-inline save"
                      onClick={handleNotesSave}
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  ref={notesContentRef}
                  className={`notes-content ${!notes ? 'empty-notes' : ''} ${notesScrollable ? 'scrollable' : ''}`}
                >
                  {notes || 'No notes - click pencil to add'}
                </div>
              )}
            </div>

            {/* Booking request info + approval actions */}
            {appointment.booking && appointment.booking.approval_status === 'pending' && (
              <div className="booking-approval-section">
                <div className="booking-info">
                  <div className="booking-info-header">Booking Request</div>
                  <div className="booking-info-row">
                    <span className="booking-info-label">From:</span>
                    <span className="booking-info-value">{appointment.booking.requested_by_name || 'Unknown'}</span>
                  </div>
                  {appointment.booking.requested_by_email && (
                    <div className="booking-info-row">
                      <span className="booking-info-label">Email:</span>
                      <span className="booking-info-value">{appointment.booking.requested_by_email}</span>
                    </div>
                  )}
                  {appointment.booking.requested_by_phone && (
                    <div className="booking-info-row">
                      <span className="booking-info-label">Phone:</span>
                      <span className="booking-info-value">{appointment.booking.requested_by_phone}</span>
                    </div>
                  )}
                  {appointment.booking.session_method && (
                    <div className="booking-info-row">
                      <span className="booking-info-label">Method:</span>
                      <span className="booking-info-value" style={{ textTransform: 'capitalize' }}>{appointment.booking.session_method}</span>
                    </div>
                  )}
                  {appointment.booking.requested_at && (
                    <div className="booking-info-row">
                      <span className="booking-info-label">Requested:</span>
                      <span className="booking-info-value">{new Date(appointment.booking.requested_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                    </div>
                  )}
                  {appointment.booking.requested_by_type === 'lead' && (
                    <div className="booking-lead-badge">New Lead</div>
                  )}
                </div>
                <div className="booking-approval-actions">
                  <button
                    className="action-btn approve"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('auth_token');
                        const res = await fetch(`/api/booking/${appointment.id}/approve`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
                        onUpdated();
                        onClose();
                      } catch (err) {
                        alert((err as Error).message);
                      }
                    }}
                    disabled={loading}
                  >
                    <Check size={14} />
                    Approve
                  </button>
                  <button
                    className="action-btn reschedule"
                    onClick={() => setIsEditing(true)}
                    disabled={loading}
                  >
                    <Pencil size={14} />
                    Reschedule
                  </button>
                  <button
                    className="action-btn deny"
                    onClick={async () => {
                      const reason = prompt('Reason for denial (optional):') || '';
                      try {
                        const token = localStorage.getItem('auth_token');
                        const res = await fetch(`/api/booking/${appointment.id}/deny`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ reason })
                        });
                        if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
                        onUpdated();
                        onClose();
                      } catch (err) {
                        alert((err as Error).message);
                      }
                    }}
                    disabled={loading}
                  >
                    <XMark size={14} />
                    Deny
                  </button>
                </div>
              </div>
            )}

            {/* Meeting attendee response buttons */}
            {isMeeting && !isOrganizer && myAttendeeRecord && (
              <div className="quick-actions">
                <span className="actions-label">Your Response:</span>
                <button
                  className={`action-btn accept ${myAttendeeRecord.status === 'confirmed' ? 'active' : ''}`}
                  onClick={() => handleAttendeeResponse('confirmed')}
                  disabled={loading}
                >
                  <Check size={14} />
                  Accept
                </button>
                <button
                  className={`action-btn tentative ${myAttendeeRecord.status === 'tentative' ? 'active' : ''}`}
                  onClick={() => handleAttendeeResponse('tentative')}
                  disabled={loading}
                >
                  <AlertCircle size={14} />
                  Tentative
                </button>
                <button
                  className={`action-btn decline ${myAttendeeRecord.status === 'declined' ? 'active' : ''}`}
                  onClick={() => handleAttendeeResponse('declined')}
                  disabled={loading}
                >
                  <XMark size={14} />
                  Decline
                </button>
              </div>
            )}

            {/* Organizer quick status actions (only for scheduled appointments) */}
            {appointment.status === 'scheduled' && (isOrganizer || !isMeeting) && (
              <div className="quick-actions">
                <span className="actions-label">Quick Actions:</span>
                <button
                  className="action-btn complete"
                  onClick={() => handleStatusChange('completed')}
                  disabled={loading}
                >
                  <Check size={14} />
                  Mark Complete
                </button>
                <button
                  className="action-btn no-show"
                  onClick={() => handleStatusChange('no-show')}
                  disabled={loading}
                >
                  <XMark size={14} />
                  No-Show
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Series Action Choice Modal */}
      {showSeriesActionModal && (
        <div className="series-action-overlay">
          <div className="series-action-modal">
            <div className="series-modal-header">
              <Repeat size={20} />
              <h3>{showSeriesActionModal === 'delete' ? 'Delete Recurring Appointment' : showSeriesActionModal === 'cancel' ? 'Cancel Recurring Appointment' : 'Edit Recurring Appointment'}</h3>
            </div>
            <p className="series-modal-description">
              This is part of a recurring series. What would you like to {showSeriesActionModal}?
            </p>
            <div className="series-action-buttons">
              <button
                className="series-action-btn just-this"
                onClick={
                  showSeriesActionModal === 'delete' ? handleDeleteJustThisOne :
                  showSeriesActionModal === 'cancel' ? handleCancelJustThisOne :
                  async () => {
                    // For "edit just this one", mark as exception first, then edit in timeline
                    setLoading(true);
                    try {
                      const token = localStorage.getItem('auth_token');
                      await fetch(`/api/appointments/${appointment.id}/mark-exception`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                    } catch {
                      // Continue with edit even if marking fails
                    }
                    setLoading(false);
                    setShowSeriesActionModal(null);
                    // Use timeline edit if available (with 'single' scope), otherwise inline form
                    if (onEditInTimeline) {
                      onEditInTimeline('single');
                    } else {
                      setIsEditing(true);
                    }
                  }
                }
                disabled={loading}
              >
                <div className="action-icon single">1</div>
                <div className="action-text">
                  <strong>Just this one</strong>
                  <span>Only {showSeriesActionModal} this appointment</span>
                </div>
              </button>
              <button
                className="series-action-btn all-future"
                onClick={
                  showSeriesActionModal === 'delete' ? handleSeriesDelete :
                  showSeriesActionModal === 'cancel' ? handleSeriesCancel :
                  () => {
                    // For edit all future, go to timeline edit mode with 'series' scope
                    console.log('[AppointmentEditModal] "This and all future" clicked - calling onEditInTimeline("series")');
                    setShowSeriesActionModal(null);
                    if (onEditInTimeline) {
                      console.log('[AppointmentEditModal] onEditInTimeline exists, calling with "series"');
                      onEditInTimeline('series');
                    } else {
                      // Fallback: show info that series editing requires Calendar view
                      setError('To reschedule the entire series, please use the Calendar view where you can drag and drop appointments.');
                    }
                  }
                }
                disabled={loading}
              >
                <div className="action-icon multiple">
                  <span>1</span>
                  <span>2</span>
                  <span>3</span>
                </div>
                <div className="action-text">
                  <strong>This and all future</strong>
                  <span>{showSeriesActionModal === 'delete' ? 'Delete' : showSeriesActionModal === 'cancel' ? 'Cancel' : 'Reschedule'} this and all following</span>
                </div>
              </button>
            </div>
            <button
              className="series-cancel-btn"
              onClick={() => setShowSeriesActionModal(null)}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <style>{`
        .appointment-edit-content {
          padding: 0;
        }

        .error-banner {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: var(--radius-medium);
          color: #ef4444;
          font-size: 13px;
          margin-bottom: 16px;
        }

        /* View Mode */
        .view-details {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .detail-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--border-secondary);
        }

        .detail-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .status-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .detail-row {
          display: flex;
          align-items: center;
          gap: 10px;
          color: var(--text-secondary);
          font-size: 14px;
        }

        .detail-row svg {
          color: var(--text-tertiary);
          flex-shrink: 0;
        }

        /* Datetime block with dual timezone support */
        .detail-row.datetime-block {
          align-items: flex-start;
        }

        .detail-row.datetime-block svg {
          margin-top: 2px;
        }

        .datetime-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .primary-time {
          color: var(--text-primary);
          font-weight: 500;
        }

        .secondary-time {
          font-size: 13px;
          font-weight: 600;
          color: var(--accent-blue);
        }

        /* Compact Participant Timeline (Read-Only) */
        .participant-timeline-compact {
          margin: 8px 0;
          padding: 12px;
          background: var(--bg-primary);
          border-radius: var(--radius-medium);
          border: 1px solid var(--border-secondary);
        }

        .timeline-hours {
          position: relative;
          height: 16px;
          margin-bottom: 4px;
          margin-left: 80px;
        }

        .participant-rows-container {
          max-height: 136px; /* ~4 rows at 34px each */
          overflow-y: auto;
        }

        .participant-rows-container::-webkit-scrollbar {
          width: 6px;
        }

        .participant-rows-container::-webkit-scrollbar-track {
          background: var(--bg-secondary);
          border-radius: 3px;
        }

        .participant-rows-container::-webkit-scrollbar-thumb {
          background: var(--border-secondary);
          border-radius: 3px;
        }

        .participant-rows-container::-webkit-scrollbar-thumb:hover {
          background: var(--text-tertiary);
        }

        .hour-mark {
          position: absolute;
          transform: translateX(-50%);
          font-size: 10px;
          color: var(--text-tertiary);
          white-space: nowrap;
        }

        .participant-row {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
        }

        .participant-row:last-child {
          margin-bottom: 0;
        }

        .participant-label {
          width: 72px;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .participant-name {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-primary);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .participant-status {
          font-size: 10px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .participant-status.confirmed { color: var(--accent-green); }
        .participant-status.pending { color: var(--accent-amber); }
        .participant-status.tentative { color: var(--accent-blue); }
        .participant-status.declined { color: var(--accent-red); }

        .participant-bar {
          flex: 1;
          height: 28px;
          position: relative;
          background: var(--bg-secondary);
          border-radius: 4px;
          overflow: hidden;
        }

        .bar-divider {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 1px;
          background: var(--border-secondary);
        }

        .bar-divider.half-hour {
          border-left: 1px dashed var(--border-secondary);
          background: transparent;
          opacity: 0.5;
        }

        .appointment-block {
          position: absolute;
          top: 4px;
          bottom: 4px;
          border-radius: 3px;
          z-index: 2;
        }

        /* Confirmed - Green (organizer or accepted) */
        .appointment-block.confirmed {
          background: rgba(16, 185, 129, 0.25);
          border: 1px solid rgba(16, 185, 129, 0.6);
        }

        /* Pending - Amber with diagonal stripes (awaiting response) */
        .appointment-block.pending {
          background: repeating-linear-gradient(
            -45deg,
            rgba(245, 158, 11, 0.25),
            rgba(245, 158, 11, 0.25) 3px,
            rgba(245, 158, 11, 0.08) 3px,
            rgba(245, 158, 11, 0.08) 6px
          );
          border: 1px solid rgba(245, 158, 11, 0.6);
        }

        /* Tentative - Blue with diagonal stripes (soft commitment) */
        .appointment-block.tentative {
          background: repeating-linear-gradient(
            -45deg,
            rgba(59, 130, 246, 0.25),
            rgba(59, 130, 246, 0.25) 3px,
            rgba(59, 130, 246, 0.08) 3px,
            rgba(59, 130, 246, 0.08) 6px
          );
          border: 1px solid rgba(59, 130, 246, 0.6);
        }

        /* Declined - Red (muted) */
        .appointment-block.declined {
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.4);
          opacity: 0.6;
        }

        .detail-notes {
          margin-top: 8px;
          padding-top: 12px;
          border-top: 1px solid var(--border-secondary);
        }

        .notes-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .notes-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 500;
          color: var(--text-tertiary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .notes-label svg {
          opacity: 0.7;
        }

        .edit-notes-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          background: transparent;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          color: var(--text-tertiary);
          cursor: pointer;
          transition: all 0.15s;
        }

        .edit-notes-btn:hover {
          background: var(--bg-secondary);
          color: var(--text-primary);
          border-color: var(--border);
        }

        .notes-content {
          margin: 0;
          font-size: 14px;
          color: var(--text-secondary);
          line-height: 1.5;
          white-space: pre-wrap;
          max-height: 200px;
          overflow-y: auto;
          padding-right: 8px;
        }

        /* Scrollable indicator - visible track when content overflows */
        .notes-content.scrollable {
          border-right: 2px solid rgba(59, 130, 246, 0.3);
          padding-right: 10px;
        }

        .notes-content::-webkit-scrollbar {
          width: 8px;
        }

        .notes-content::-webkit-scrollbar-track {
          background: rgba(59, 130, 246, 0.1);
          border-radius: 4px;
        }

        .notes-content::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.4);
          border-radius: 4px;
        }

        .notes-content::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.6);
        }

        .notes-content.empty-notes {
          color: var(--text-tertiary);
          font-style: italic;
        }

        .notes-edit-inline {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .textarea-container {
          position: relative;
          display: flex;
        }

        .notes-edit-inline textarea {
          width: 100%;
          padding: 10px 12px;
          padding-bottom: 18px;
          background: var(--bg-primary);
          border: 1px solid var(--accent-green);
          border-radius: var(--radius-small);
          color: var(--text-primary);
          font-size: 14px;
          font-family: inherit;
          line-height: 1.5;
          resize: vertical;
          min-height: 80px;
          max-height: 300px;
          overflow-y: hidden;
          transition: height 0.1s ease-out;
        }

        .notes-edit-inline textarea:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
        }

        /* Visible resize grip in bottom-right corner */
        .resize-grip {
          position: absolute;
          bottom: 4px;
          right: 4px;
          color: rgba(16, 185, 129, 0.7);
          pointer-events: none;
        }

        .textarea-container:hover .resize-grip {
          color: rgba(16, 185, 129, 1);
        }

        .notes-edit-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }

        .btn-inline {
          padding: 6px 12px;
          border-radius: var(--radius-small);
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .btn-inline.cancel {
          background: transparent;
          border: 1px solid var(--border);
          color: var(--text-secondary);
        }

        .btn-inline.cancel:hover:not(:disabled) {
          background: var(--bg-secondary);
        }

        .btn-inline.save {
          background: var(--accent-green);
          border: none;
          color: white;
        }

        .btn-inline.save:hover:not(:disabled) {
          background: #059669;
        }

        .btn-inline:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .booking-approval-section {
          background: rgba(245, 158, 11, 0.08);
          border: 1px solid rgba(245, 158, 11, 0.25);
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 16px;
        }
        .booking-info {
          margin-bottom: 12px;
        }
        .booking-info-header {
          font-weight: 700;
          font-size: 14px;
          color: #f59e0b;
          margin-bottom: 8px;
        }
        .booking-info-row {
          display: flex;
          gap: 8px;
          font-size: 13px;
          padding: 2px 0;
        }
        .booking-info-label {
          color: var(--text-tertiary);
          min-width: 70px;
        }
        .booking-info-value {
          color: var(--text-primary);
          font-weight: 500;
        }
        .booking-lead-badge {
          display: inline-block;
          margin-top: 8px;
          padding: 2px 10px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
          background: rgba(59, 130, 246, 0.15);
          color: #3b82f6;
        }
        .booking-approval-actions {
          display: flex;
          gap: 8px;
        }
        .action-btn.approve {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
          border: 1px solid rgba(16, 185, 129, 0.3);
          flex: 1;
          justify-content: center;
        }
        .action-btn.approve:hover {
          background: rgba(16, 185, 129, 0.25);
        }
        .action-btn.reschedule {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          border: 1px solid rgba(59, 130, 246, 0.2);
          flex: 0;
        }
        .action-btn.reschedule:hover {
          background: rgba(59, 130, 246, 0.2);
        }
        .action-btn.deny {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.2);
          flex: 0;
        }
        .action-btn.deny:hover {
          background: rgba(239, 68, 68, 0.2);
        }

        .quick-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          padding-top: 14px;
          margin-top: 8px;
          border-top: 1px solid var(--border-secondary);
        }

        .actions-label {
          font-size: 12px;
          color: var(--text-tertiary);
          margin-right: 4px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 6px 12px;
          border: none;
          border-radius: var(--radius-small);
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .action-btn.complete {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .action-btn.complete:hover {
          background: rgba(16, 185, 129, 0.2);
        }

        .action-btn.no-show {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .action-btn.no-show:hover {
          background: rgba(245, 158, 11, 0.2);
        }

        .action-btn.accept {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }
        .action-btn.accept:hover, .action-btn.accept.active {
          background: rgba(16, 185, 129, 0.25);
        }
        .action-btn.tentative {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }
        .action-btn.tentative:hover, .action-btn.tentative.active {
          background: rgba(59, 130, 246, 0.25);
        }
        .action-btn.decline {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }
        .action-btn.decline:hover, .action-btn.decline.active {
          background: rgba(239, 68, 68, 0.25);
        }
        .action-btn.active {
          font-weight: 700;
          border-width: 2px;
        }

        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Edit Form */
        .edit-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group label {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 10px 12px;
          background: var(--bg-primary);
          border: 1px solid var(--border);
          border-radius: var(--radius-small);
          color: var(--text-primary);
          font-size: 14px;
          font-family: inherit;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--accent-green);
        }

        .form-group textarea {
          resize: vertical;
          min-height: 80px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        /* Clean Outlook-style DateTime Picker */
        .datetime-picker-group {
          background: var(--bg-primary);
          border: 1px solid var(--border);
          border-radius: var(--radius-medium);
          padding: 12px;
        }

        .datetime-picker-group .datetime-row {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
        }

        .datetime-picker-group .datetime-row:last-of-type {
          margin-bottom: 8px;
        }

        .datetime-picker-group .datetime-label {
          width: 40px;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .datetime-picker-group .datetime-date {
          flex: 1;
          padding: 8px 10px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          color: var(--text-primary);
          font-size: 13px;
          cursor: pointer;
        }

        .datetime-picker-group .datetime-date:focus {
          outline: none;
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
        }

        /* Custom Time Dropdown */
        .time-dropdown {
          position: relative;
          width: 110px;
        }

        .time-dropdown-trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 8px 10px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          color: var(--text-primary);
          font-size: 13px;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.15s;
        }

        .time-dropdown-trigger:hover {
          border-color: var(--border);
        }

        .time-dropdown-trigger.open {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
        }

        .dropdown-chevron {
          color: var(--text-tertiary);
          transition: transform 0.15s;
          flex-shrink: 0;
        }

        .dropdown-chevron.rotated {
          transform: rotate(180deg);
        }

        .time-dropdown-list {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          max-height: 200px;
          overflow-y: auto;
          background: var(--bg-secondary);
          border: 1px solid var(--border);
          border-radius: var(--radius-small);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          z-index: 100;
        }

        .time-dropdown-list::-webkit-scrollbar {
          width: 6px;
        }

        .time-dropdown-list::-webkit-scrollbar-track {
          background: transparent;
        }

        .time-dropdown-list::-webkit-scrollbar-thumb {
          background: var(--border);
          border-radius: 3px;
        }

        .time-dropdown-list::-webkit-scrollbar-thumb:hover {
          background: var(--text-tertiary);
        }

        .time-dropdown-item {
          padding: 8px 12px;
          font-size: 13px;
          color: var(--text-primary);
          cursor: pointer;
          transition: background 0.1s;
        }

        .time-dropdown-item:hover {
          background: var(--bg-primary);
        }

        .time-dropdown-item.selected {
          background: rgba(59, 130, 246, 0.15);
          color: var(--accent-blue);
          font-weight: 500;
        }

        .time-dropdown-item.selected:hover {
          background: rgba(59, 130, 246, 0.2);
        }

        .datetime-picker-group .datetime-time {
          width: 110px;
        }

        .duration-indicator {
          display: flex;
          align-items: center;
          gap: 6px;
          padding-top: 8px;
          border-top: 1px solid var(--border-secondary);
          font-size: 12px;
          color: var(--text-tertiary);
        }

        .duration-indicator svg {
          opacity: 0.7;
        }

        .duration-indicator .tz-hint {
          margin-left: auto;
          font-size: 11px;
        }

        /* Conflict Warning */
        .conflict-warning {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 8px;
          padding: 8px 10px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: var(--radius-small);
          color: #ef4444;
          font-size: 12px;
          font-weight: 500;
        }

        .conflict-warning svg {
          flex-shrink: 0;
        }

        /* Visual Scheduling Timeline - Outlook-style Busy Bar */
        .scheduling-timeline {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid var(--border-secondary);
        }

        .timeline-header {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 500;
          color: var(--text-tertiary);
          margin-bottom: 10px;
        }

        .timeline-header svg {
          opacity: 0.7;
        }

        .timeline-header .loading-indicator {
          margin-left: auto;
          color: var(--accent-blue);
          animation: pulse 1s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }

        .timeline-bar-container {
          position: relative;
          margin-bottom: 10px;
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: thin;
          scrollbar-color: var(--text-tertiary) var(--bg-primary);
          color-scheme: dark light;
        }

        .timeline-bar-container::-webkit-scrollbar {
          height: 6px;
          background: var(--bg-primary);
        }

        .timeline-bar-container::-webkit-scrollbar-track {
          background: inherit;
          border-radius: 3px;
        }

        .timeline-bar-container::-webkit-scrollbar-thumb {
          background: var(--text-tertiary);
          border-radius: 3px;
        }

        .timeline-bar-container::-webkit-scrollbar-thumb:hover {
          background: var(--border);
        }

        .timeline-inner {
          position: relative;
          min-width: 100%;
        }

        .timeline-markers {
          position: relative;
          height: 16px;
          margin-bottom: 4px;
        }

        .hour-marker {
          position: absolute;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .hour-marker::after {
          content: '';
          width: 1px;
          height: 4px;
          background: var(--border);
          margin-top: 2px;
        }

        .marker-label {
          font-size: 9px;
          color: var(--text-tertiary);
          font-weight: 500;
        }

        .timeline-bar {
          position: relative;
          height: 28px;
          background: var(--bg-primary);
          border: 1px solid var(--border);
          border-radius: var(--radius-small);
          overflow: hidden;
        }

        .timeline-divider {
          position: absolute;
          top: 0;
          height: 100%;
          width: 1px;
          background: var(--border-secondary);
          z-index: 0;
        }

        .timeline-divider.half-hour {
          background: transparent;
          border-left: 1px dotted var(--border-secondary);
          opacity: 0.6;
        }

        .timeline-busy {
          position: absolute;
          top: 0;
          height: 100%;
          background: rgba(107, 114, 128, 0.4);
          border-left: 2px solid rgba(107, 114, 128, 0.7);
          z-index: 1;
        }

        .timeline-busy:hover {
          background: rgba(107, 114, 128, 0.5);
        }

        .timeline-proposed {
          position: absolute;
          top: 0;
          height: 100%;
          background: rgba(16, 185, 129, 0.35);
          border: 2px solid var(--accent-green);
          border-radius: 3px;
          z-index: 2;
          box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.15);
          cursor: grab;
          user-select: none;
        }

        .timeline-proposed:hover {
          background: rgba(16, 185, 129, 0.45);
        }

        .timeline-proposed.dragging {
          cursor: grabbing;
          background: rgba(16, 185, 129, 0.5);
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.25);
        }

        .timeline-proposed.resizing {
          cursor: ew-resize;
        }

        .timeline-proposed.has-conflict {
          background: rgba(239, 68, 68, 0.35);
          border-color: #ef4444;
          box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.15);
        }

        .timeline-proposed.has-conflict:hover {
          background: rgba(239, 68, 68, 0.45);
        }

        .timeline-proposed.has-conflict.dragging {
          background: rgba(239, 68, 68, 0.5);
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.25);
        }

        /* Resize handles */
        .resize-handle {
          position: absolute;
          top: 0;
          width: 8px;
          height: 100%;
          cursor: ew-resize;
          z-index: 3;
        }

        .resize-handle-left {
          left: -4px;
        }

        .resize-handle-right {
          right: -4px;
        }

        .resize-handle::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 3px;
          height: 14px;
          background: var(--accent-green);
          border-radius: 2px;
          opacity: 0;
          transition: opacity 0.15s;
        }

        .timeline-proposed:hover .resize-handle::after,
        .timeline-proposed.resizing .resize-handle::after {
          opacity: 0.8;
        }

        .timeline-proposed.has-conflict .resize-handle::after {
          background: #ef4444;
        }

        .timeline-legend {
          display: flex;
          align-items: center;
          gap: 16px;
          font-size: 10px;
          color: var(--text-tertiary);
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .legend-item.free {
          color: var(--accent-green);
        }

        .legend-item.free svg {
          opacity: 0.8;
        }

        .legend-swatch {
          width: 12px;
          height: 10px;
          border-radius: 2px;
        }

        .legend-swatch.proposed {
          background: rgba(16, 185, 129, 0.35);
          border: 1px solid var(--accent-green);
        }

        .legend-swatch.busy {
          background: rgba(107, 114, 128, 0.4);
          border-left: 2px solid rgba(107, 114, 128, 0.7);
        }

        /* Footer */
        .delete-confirm-footer {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
        }

        .confirm-text {
          flex: 1;
          font-size: 14px;
          color: var(--text-secondary);
        }

        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px 16px;
          border: none;
          border-radius: var(--radius-small);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background: var(--accent-green);
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #059669;
        }

        .btn-secondary {
          background: var(--bg-primary);
          color: var(--text-secondary);
          border: 1px solid var(--border);
        }

        .btn-secondary:hover:not(:disabled) {
          background: var(--bg-secondary);
          color: var(--text-primary);
        }

        .btn-danger {
          background: #ef4444;
          color: white;
        }

        .btn-danger:hover:not(:disabled) {
          background: #dc2626;
        }

        .btn-danger-filled {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border: none;
        }

        .btn-danger-filled:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.2);
        }

        .btn-warning {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
          border: none;
        }

        .btn-warning:hover:not(:disabled) {
          background: rgba(245, 158, 11, 0.2);
        }

        /* Series badge */
        .detail-row.series-badge {
          color: var(--accent-green);
          background: rgba(16, 185, 129, 0.1);
          padding: 8px 12px;
          border-radius: var(--radius-small);
          border: 1px solid rgba(16, 185, 129, 0.2);
        }

        .detail-row.series-badge svg {
          color: var(--accent-green);
        }

        /* Series Action Modal Overlay */
        .series-action-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }

        .series-action-modal {
          background: var(--bg-secondary);
          border-radius: var(--radius-large);
          padding: 24px;
          width: 90%;
          max-width: 400px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
        }

        .series-modal-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
        }

        .series-modal-header svg {
          color: var(--accent-green);
        }

        .series-modal-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .series-modal-description {
          color: var(--text-secondary);
          font-size: 13px;
          margin: 0 0 20px;
        }

        .series-action-buttons {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 16px;
        }

        .series-action-btn {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 16px;
          background: var(--bg-primary);
          border: 1px solid var(--border);
          border-radius: var(--radius-medium);
          cursor: pointer;
          text-align: left;
          transition: all 0.15s;
        }

        .series-action-btn:hover:not(:disabled) {
          border-color: var(--accent-green);
          background: rgba(16, 185, 129, 0.05);
        }

        .series-action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .series-action-btn .action-icon {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .series-action-btn .action-icon.single {
          background: rgba(59, 130, 246, 0.15);
          color: #3b82f6;
          font-weight: 700;
          font-size: 18px;
        }

        .series-action-btn .action-icon.multiple {
          background: rgba(16, 185, 129, 0.15);
          color: var(--accent-green);
          flex-direction: column;
          font-size: 10px;
          font-weight: 600;
          gap: 1px;
        }

        .series-action-btn .action-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .series-action-btn .action-text strong {
          font-size: 14px;
          color: var(--text-primary);
        }

        .series-action-btn .action-text span {
          font-size: 12px;
          color: var(--text-tertiary);
        }

        .series-cancel-btn {
          width: 100%;
          padding: 10px 16px;
          background: transparent;
          border: 1px solid var(--border);
          border-radius: var(--radius-medium);
          color: var(--text-secondary);
          font-size: 13px;
          cursor: pointer;
          transition: all 0.15s;
        }

        .series-cancel-btn:hover:not(:disabled) {
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .series-cancel-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Tooltip for participant timeline */
        .participant-tooltip {
          position: fixed;
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          padding: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          z-index: 1100;
          max-width: 280px;
          pointer-events: none;
        }

        .participant-tooltip .tooltip-title {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .participant-tooltip .tooltip-status {
          font-size: 12px;
          color: var(--text-secondary);
          margin-bottom: 8px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border-secondary);
        }

        .participant-tooltip .tooltip-times {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .participant-tooltip .tooltip-time {
          display: flex;
          gap: 8px;
          font-size: 12px;
        }

        .participant-tooltip .tooltip-time .label {
          color: var(--text-secondary);
          min-width: 50px;
        }

        .participant-tooltip .tooltip-time span:last-child {
          color: var(--text-primary);
        }

        .appointment-block {
          cursor: pointer;
        }
      `}</style>

      {/* Tooltip for participant timeline (rendered outside to avoid clipping) */}
      {tooltip.visible && (
        <div
          className="participant-tooltip"
          style={{
            left: `${tooltip.x + 10}px`,
            top: `${tooltip.y + 10}px`
          }}
        >
          <div className="tooltip-title">{tooltip.participantName}</div>
          <div className="tooltip-status">{tooltip.status}</div>
          <div className="tooltip-times">
            <div className="tooltip-time">
              <span className="label">Coach:</span>
              <span>
                {new Date(appointment.start_time).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZone: coachTimezone
                })} - {new Date(appointment.end_time).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZone: coachTimezone
                })} {(() => {
                  const abbrevMap: Record<string, string> = {
                    'America/New_York': 'EST', 'America/Chicago': 'CST',
                    'America/Denver': 'MST', 'America/Los_Angeles': 'PST',
                    'America/Phoenix': 'MST', 'Pacific/Honolulu': 'HST'
                  };
                  return abbrevMap[coachTimezone] || coachTimezone.split('/').pop()?.replace('_', ' ') || coachTimezone;
                })()}
              </span>
            </div>
            <div className="tooltip-time">
              <span className="label">{clientName.split(' ')[0]}:</span>
              <span>
                {new Date(appointment.start_time).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZone: clientTimezone
                })} - {new Date(appointment.end_time).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZone: clientTimezone
                })} {(() => {
                  const abbrevMap: Record<string, string> = {
                    'America/New_York': 'EST', 'America/Chicago': 'CST',
                    'America/Denver': 'MST', 'America/Los_Angeles': 'PST',
                    'America/Phoenix': 'MST', 'Pacific/Honolulu': 'HST'
                  };
                  return abbrevMap[clientTimezone] || clientTimezone.split('/').pop()?.replace('_', ' ') || clientTimezone;
                })()}
              </span>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AppointmentEditModal;
